import Link from "next/link";
import { ChevronTextSmallLeft } from "src/icons/ChevronTextSmallLeft";
import { CategoryTreeSkeleton } from "./CategoryTreeSkeleton";
import { useCategoryLevelView } from "./use-category-level-view";
import {
  CategoryRailBack,
  CategoryRailLink,
  CategoryRailList,
  CategoryRailNav,
} from "./CategoryTree.styled";
import type { Gender, ProductListType } from "src/types/product-listing";
import { HEADER_STICKY_OFFSET } from "src/utils/constants";

type CategoryTreeProps = {
  gender?: Gender;
  categorySlug?: string;
  pageContentType?: ProductListType;
  handleScrollToTop?: () => void;
  stickyTop?: number;
};

function CategoryTree({
  gender,
  categorySlug,
  pageContentType,
  handleScrollToTop,
  stickyTop = HEADER_STICKY_OFFSET,
}: CategoryTreeProps): JSX.Element | null {
  const { view, isReady, drillsByFilter } = useCategoryLevelView({
    gender,
    categorySlug,
    pageContentType,
  });

  if (!isReady) {
    return <CategoryTreeSkeleton />;
  }

  if (!view.items.length && !view.back) {
    return null;
  }

  const backLabel = view.back?.label ?? "";
  const scroll = !drillsByFilter;
  const onNavigate = drillsByFilter ? handleScrollToTop : undefined;
  const rowLabel =
    drillsByFilter && !view.isLeaf
      ? view.current?.label ?? backLabel
      : backLabel;

  return (
    <CategoryRailNav aria-label="Categories" stickyTop={stickyTop}>
      {view.back ? (
        <Link
          href={view.back.href}
          prefetch={false}
          scroll={scroll}
          passHref
          legacyBehavior
        >
          <CategoryRailBack
            aria-label={`Back to ${backLabel}`}
            onClick={onNavigate}
          >
            <ChevronTextSmallLeft />
            <span>{rowLabel}</span>
          </CategoryRailBack>
        </Link>
      ) : null}

      <CategoryRailList>
        {view.items.map((item) => (
          <li key={item.itemId}>
            <Link
              href={item.href}
              prefetch={false}
              scroll={scroll}
              passHref
              legacyBehavior
            >
              <CategoryRailLink
                isActive={item.isActive}
                aria-current={item.isActive ? "page" : undefined}
                onClick={onNavigate}
              >
                {item.label}
              </CategoryRailLink>
            </Link>
          </li>
        ))}
      </CategoryRailList>
    </CategoryRailNav>
  );
}

export { CategoryTree };
